import { bindShopifyCaptchaSubmit } from '@theme/topeak-shopify-captcha';

const CONTACT_SUBMIT_KEY = 'topeak-contact-form-submitted';
const TOAST_VISIBLE_CLASS = 'is-visible';
const TOAST_DURATION_MS = 4200;

let contactFormInitialized = false;

/** @type {number | undefined} */
let hideTimeoutId;

/** @type {number} */
let hideDeadline = 0;

/** @type {number} */
let remainingMs = TOAST_DURATION_MS;

/**
 * @param {HTMLFormElement} form
 */
function setContactFormLoading(form) {
  const submit = form.querySelector('.topeak-btn--contact-submit');
  const submitText = form.querySelector('.topeak-contact-layout__submit-text');

  if (!(submit instanceof HTMLButtonElement) || !(submitText instanceof HTMLElement)) return;

  const loadingLabel = submit.dataset.loadingLabel || 'Submitting...';

  submit.setAttribute('aria-busy', 'true');
  submit.classList.add('topeak-btn--loading');
  submitText.textContent = loadingLabel;
  form.setAttribute('aria-busy', 'true');
}

/**
 * @param {HTMLElement} progressBar
 */
function restartProgressBar(progressBar) {
  progressBar.style.animation = 'none';
  progressBar.getBoundingClientRect();
  progressBar.style.removeProperty('animation');
}

/**
 * @param {HTMLElement} toast
 * @param {number} delay
 */
function scheduleHide(toast, delay) {
  if (hideTimeoutId) {
    window.clearTimeout(hideTimeoutId);
  }

  hideDeadline = Date.now() + delay;
  remainingMs = delay;

  hideTimeoutId = window.setTimeout(() => {
    hideToast(toast);
  }, delay);
}

/**
 * @param {HTMLElement} toast
 */
function pauseHide(toast) {
  if (!toast.classList.contains(TOAST_VISIBLE_CLASS)) {
    return;
  }

  if (hideTimeoutId) {
    window.clearTimeout(hideTimeoutId);
    hideTimeoutId = undefined;
    remainingMs = Math.max(0, hideDeadline - Date.now());
  }

  const progressBar = toast.querySelector('[data-topeak-contact-toast-progress]');

  if (progressBar instanceof HTMLElement) {
    progressBar.style.animationPlayState = 'paused';
  }
}

/**
 * @param {HTMLElement} toast
 */
function resumeHide(toast) {
  if (!toast.classList.contains(TOAST_VISIBLE_CLASS)) {
    return;
  }

  const progressBar = toast.querySelector('[data-topeak-contact-toast-progress]');

  if (progressBar instanceof HTMLElement) {
    progressBar.style.animationPlayState = 'running';
  }

  scheduleHide(toast, remainingMs);
}

/**
 * @param {HTMLElement} toast
 */
function hideToast(toast) {
  toast.classList.remove(TOAST_VISIBLE_CLASS);
  toast.hidden = true;

  if (hideTimeoutId) {
    window.clearTimeout(hideTimeoutId);
    hideTimeoutId = undefined;
  }
}

/**
 * @param {HTMLElement} toast
 * @param {string} message
 */
function showToast(toast, message) {
  const messageElement = toast.querySelector('[data-topeak-contact-toast-message]');
  const progressBar = toast.querySelector('[data-topeak-contact-toast-progress]');

  if (messageElement) {
    messageElement.textContent = message;
  }

  if (progressBar instanceof HTMLElement) {
    restartProgressBar(progressBar);
    progressBar.style.animationPlayState = 'running';
  }

  toast.hidden = false;
  toast.classList.add(TOAST_VISIBLE_CLASS);
  scheduleHide(toast, TOAST_DURATION_MS);
}

/**
 * @param {HTMLElement} toast
 */
function bindToastInteractions(toast) {
  const closeButton = toast.querySelector('[data-topeak-contact-toast-close]');

  closeButton?.addEventListener('click', () => {
    hideToast(toast);
  });

  toast.addEventListener('mouseenter', () => {
    pauseHide(toast);
  });

  toast.addEventListener('mouseleave', () => {
    resumeHide(toast);
  });

  toast.addEventListener('focusin', () => {
    pauseHide(toast);
  });

  toast.addEventListener('focusout', (event) => {
    if (event.relatedTarget instanceof Node && toast.contains(event.relatedTarget)) {
      return;
    }

    resumeHide(toast);
  });
}

function isContactPostedSuccess() {
  return new URLSearchParams(window.location.search).get('contact_posted') === 'true';
}

function clearContactPostedParam() {
  const url = new URL(window.location.href);

  if (!url.searchParams.has('contact_posted')) {
    return;
  }

  url.searchParams.delete('contact_posted');
  window.history.replaceState({}, '', url);
}

function handleContactSuccessToast() {
  const submittedFormId = sessionStorage.getItem(CONTACT_SUBMIT_KEY);
  const postedViaUrl = isContactPostedSuccess();

  if (!submittedFormId && !postedViaUrl) {
    return;
  }

  const toast = document.querySelector('[data-topeak-contact-toast]');

  if (!(toast instanceof HTMLElement)) {
    return;
  }

  if (submittedFormId) {
    sessionStorage.removeItem(CONTACT_SUBMIT_KEY);
  }

  const successMessage = document.querySelector('.topeak-contact-layout__message--success');
  const defaultMessage =
    toast.querySelector('[data-topeak-contact-toast-message]')?.textContent?.trim() ||
    "Thanks for contacting us. We'll get back to you as soon as possible.";
  const message = successMessage?.textContent?.trim() || defaultMessage;

  if (successMessage instanceof HTMLElement) {
    successMessage.hidden = true;
  }

  showToast(toast, message);
  clearContactPostedParam();
}

/**
 * Shows a loading state on the contact form while Shopify processes submission.
 */
export function initContactFormSubmitLoading() {
  if (contactFormInitialized) return;

  contactFormInitialized = true;

  const toast = document.querySelector('[data-topeak-contact-toast]');

  if (toast instanceof HTMLElement) {
    bindToastInteractions(toast);
    handleContactSuccessToast();
  }

  const form = document.querySelector('.topeak-contact-layout__form');

  if (form instanceof HTMLFormElement) {
    bindShopifyCaptchaSubmit(form, () => {
      try {
        sessionStorage.setItem(CONTACT_SUBMIT_KEY, form.id);
      } catch {
        /* ignore storage failures */
      }

      setContactFormLoading(form);
    });
  }
}

initContactFormSubmitLoading();
